import { useMemo } from 'react';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useCheckSMSPreferences } from '@frontend/api-comm-preference';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import type { GroupParticipant } from '@frontend/thread-header-context';

type UseGroupOptOutStatusArgs = {
  participants: GroupParticipant[];
  locationId: string;
  enabled?: boolean;
};

type GroupOptOutStatus = {
  /** Whether any participant has opted out */
  hasOptedOutParticipants: boolean;
  /** Participants who have opted out */
  optedOutParticipants: GroupParticipant[];
  /** Participants who have NOT opted out (still active) */
  activeParticipants: GroupParticipant[];
  /** Whether the queries are still loading */
  isLoading: boolean;
};

/**
 * Checks the SMS opt-out status for all participants in a group thread.
 * Uses the batch `useCheckSMSPreferences` hook which leverages react-query
 * deduplication — the same query keys will share cache across components.
 */
export const useGroupOptOutStatus = ({
  participants,
  locationId,
  enabled = true,
}: UseGroupOptOutStatusArgs): GroupOptOutStatus => {
  const requests = useMemo(
    () =>
      enabled
        ? participants
            .filter((p) => !!p.phoneNumber)
            .map((p) => ({
              request: {
                userChannelAddress: formatPhoneNumberE164(p.phoneNumber!),
                channel: Channel.CHANNEL_SMS,
                locationId,
                messageType: MessageType_Enum.MESSAGING_MANUAL,
              },
            }))
        : [],
    [participants, locationId, enabled]
  );

  const results = useCheckSMSPreferences(requests);

  return useMemo(() => {
    if (!enabled || participants.length === 0) {
      return {
        hasOptedOutParticipants: false,
        optedOutParticipants: [],
        activeParticipants: participants,
        isLoading: false,
      };
    }

    const participantsWithPhone = participants.filter((p) => !!p.phoneNumber);
    const participantsWithoutPhone = participants.filter((p) => !p.phoneNumber);

    const isLoading = results.some((r) => r.isLoading);

    const optedOutParticipants: GroupParticipant[] = [];
    const activeParticipants: GroupParticipant[] = [...participantsWithoutPhone];

    participantsWithPhone.forEach((participant, index) => {
      const result = results[index];
      if (result?.data?.consented === false) {
        optedOutParticipants.push(participant);
      } else {
        activeParticipants.push(participant);
      }
    });

    return {
      hasOptedOutParticipants: optedOutParticipants.length > 0,
      optedOutParticipants,
      activeParticipants,
      isLoading,
    };
  }, [enabled, participants, results]);
};
